import React, { useMemo, useState } from "react";
import { Mail, User, Clock3, BriefcaseBusiness, Upload } from "lucide-react";
import "./HRProfilePage.css";
import { memoryStorage } from "../../utils/memoryStorage";

const HRProfilePage = () => {
  const [activeTab, setActiveTab] = useState("profile");
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [message, setMessage] = useState("");

  const profile = useMemo(() => {
    let user = null;
    try {
      user = JSON.parse(memoryStorage.getItem("user")) || {};
    } catch (error) {
      user = {};
    }

    return {
      name: user.name || user.fullName || "hr@blackroth.in",
      email: user.email || "hr@blackroth.in",
      timezone:
        user.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        "Indian/Maldives",
      biography: user.biography || "-",
    };
  }, []);

  const [savedProfile, setSavedProfile] = useState(profile);
  const [formData, setFormData] = useState(profile);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditInfoClick = () => {
    let existingUser = {};
    try {
      existingUser = JSON.parse(memoryStorage.getItem("user")) || {};
    } catch (error) {
      existingUser = {};
    }
    const updatedUser = { ...existingUser, ...formData };
    memoryStorage.setItem("user", JSON.stringify(updatedUser));
    setSavedProfile(formData);
    setIsEditing(false);
    setMessage("Profile info updated successfully.");
  };

  const handleCancelEdit = () => {
    setFormData(savedProfile);
    setIsEditing(false);
    setMessage("");
  };

  const handlePasswordChange = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdatePassword = () => {
    setMessage("");
    if (
      !passwordForm.currentPassword ||
      !passwordForm.newPassword ||
      !passwordForm.confirmPassword
    ) {
      setMessage("Please fill all password fields.");
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setMessage("New password must be at least 6 characters.");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage("New password and confirm password do not match.");
      return;
    }

    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setShowPasswordForm(false);
    setMessage("Password updated successfully.");
  };

  return (
    <section className="hr-profile-wrap">
      <div
        className="hr-profile-tabs"
        role="tablist"
        aria-label="HR Profile Tabs"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "profile"}
          className={`hr-profile-tab ${activeTab === "profile" ? "active" : ""}`}
          onClick={() => setActiveTab("profile")}
        >
          Profile
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "history"}
          className={`hr-profile-tab ${activeTab === "history" ? "active" : ""}`}
          onClick={() => setActiveTab("history")}
        >
          Login History
        </button>
      </div>

      {activeTab === "profile" ? (
        <article className="hr-profile-card">
          <h2>Account Details</h2>
          <div className="hr-profile-grid">
            <div
              className="hr-upload-box"
              role="button"
              tabIndex={0}
              aria-label="Upload profile image"
            >
              <Upload size={24} />
              <strong>Upload</strong>
              <p>Accepts .jpg, .jpeg, .png files</p>
            </div>

            <div className="hr-detail">
              <label>
                <User size={18} /> Name
              </label>
              <p>{savedProfile.name}</p>
            </div>

            <div className="hr-detail">
              <label>
                <Mail size={18} /> Email Address
              </label>
              <p>{savedProfile.email}</p>
            </div>

            <div className="hr-detail">
              <label>
                <Clock3 size={18} /> Timezone
              </label>
              <p>{savedProfile.timezone}</p>
            </div>

            <div className="hr-detail">
              <label>
                <BriefcaseBusiness size={18} /> Biography
              </label>
              <p>{savedProfile.biography}</p>
            </div>
          </div>

          {isEditing && (
            <div className="hr-edit-modal">
              <div className="hr-edit-modal-card">
                <h3>Edit Account Info</h3>
                <div className="hr-edit-grid">
                  <input
                    type="text"
                    className="hr-input"
                    placeholder="Name"
                    value={formData.name}
                    onChange={(event) =>
                      handleFieldChange("name", event.target.value)
                    }
                  />
                  <input
                    type="email"
                    className="hr-input"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(event) =>
                      handleFieldChange("email", event.target.value)
                    }
                  />
                  <input
                    type="text"
                    className="hr-input"
                    placeholder="Timezone"
                    value={formData.timezone}
                    onChange={(event) =>
                      handleFieldChange("timezone", event.target.value)
                    }
                  />
                  <textarea
                    className="hr-input hr-textarea"
                    placeholder="Biography"
                    value={formData.biography}
                    onChange={(event) =>
                      handleFieldChange("biography", event.target.value)
                    }
                  />
                </div>
                <div className="hr-edit-actions">
                  <button
                    type="button"
                    className="hr-btn hr-btn-ash"
                    onClick={handleCancelEdit}
                  >
                    Cancel Edit
                  </button>
                  <button
                    type="button"
                    className="hr-btn hr-btn-solid"
                    onClick={handleEditInfoClick}
                  >
                    Save Info
                  </button>
                </div>
              </div>
            </div>
          )}

          {showPasswordForm && (
            <div className="hr-password-modal">
              <div className="hr-password-modal-card">
                <h3>Change Password</h3>
                <div className="hr-password-grid">
                  <input
                    type="password"
                    className="hr-input"
                    placeholder="Current Password"
                    value={passwordForm.currentPassword}
                    onChange={(event) =>
                      handlePasswordChange(
                        "currentPassword",
                        event.target.value,
                      )
                    }
                  />
                  <input
                    type="password"
                    className="hr-input"
                    placeholder="New Password"
                    value={passwordForm.newPassword}
                    onChange={(event) =>
                      handlePasswordChange("newPassword", event.target.value)
                    }
                  />
                  <input
                    type="password"
                    className="hr-input"
                    placeholder="Confirm New Password"
                    value={passwordForm.confirmPassword}
                    onChange={(event) =>
                      handlePasswordChange(
                        "confirmPassword",
                        event.target.value,
                      )
                    }
                  />
                </div>
                <div className="hr-password-actions">
                  <button
                    type="button"
                    className="hr-btn hr-btn-ash"
                    onClick={() => setShowPasswordForm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="hr-btn hr-btn-solid"
                    onClick={handleUpdatePassword}
                  >
                    Update Password
                  </button>
                </div>
              </div>
            </div>
          )}

          {message && <p className="hr-profile-message">{message}</p>}

          <div className="hr-profile-actions">
            {!isEditing && (
              <button
                type="button"
                className="hr-btn hr-btn-outline"
                onClick={() => {
                  setMessage("");
                  setShowPasswordForm((prev) => !prev);
                }}
              >
                Change Password
              </button>
            )}
            {!isEditing && (
              <button
                type="button"
                className="hr-btn hr-btn-solid"
                onClick={() => {
                  setMessage("");
                  setShowPasswordForm(false);
                  setFormData(savedProfile);
                  setIsEditing(true);
                }}
              >
                Edit Info
              </button>
            )}
          </div>
        </article>
      ) : (
        <article className="hr-profile-card">
          <h2>Login History</h2>
          <div className="hr-history-row">
            <span>Last Login</span>
            <strong>{new Date().toLocaleString()}</strong>
          </div>
          <div className="hr-history-row">
            <span>Device</span>
            <strong>Web Browser</strong>
          </div>
          <div className="hr-history-row">
            <span>Status</span>
            <strong>Successful</strong>
          </div>
        </article>
      )}
    </section>
  );
};

export default HRProfilePage;
 